import { ReactElement, useEffect } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Loading, Tabs, Tab } from "@wfp/ui"
import { useAppSelector } from "hooks/hooksStore"
import { permissionsHH } from "constants/household"
import { SurveyStatus } from "constants/constants"
import { selectUser } from "features/common/commonSlice"
import { useGetSurveyHHQuery } from "services/household/survey"
import { checkAccess } from "utils/helpers"
import ErrorState from "components/Shared/EmptyState/ErrorState"
import SurveyDetails from "components/Household/SurveyDetails/SurveyDetails"
import SurveyDataLoader from "components/Household/SurveyDetails/SurveyDataLoader"
import SurveyViewData from "components/Household/SurveyDetails/SurveyViewData"

const SurveyDetailsPage = (): ReactElement => {
  const navigate = useNavigate()
  const { search, hash } = useLocation()
  const mode = new URLSearchParams(search)?.get("mode") ?? "view"
  const { surveyId } = useParams<{ surveyId: string }>()
  const user = useAppSelector(selectUser)

  const {
    data: survey,
    isLoading: isLoadingSurvey,
    error: errorSurvey,
  } = useGetSurveyHHQuery(Number(surveyId), { skip: !surveyId })

  const changeMode = (value: string): void => {
    if (mode === "edit") {
      navigate(`/household/surveys/${survey?.surveyId}${value}`)
    } else {
      navigate(`/household/surveys/${survey?.surveyId}?mode=edit${value}`)
    }
  }

  const selectTab = (): number => {
    switch (hash) {
      case "#details":
        return 0
      case "#data-loader":
        return 1
      case "#data":
        return 2
      default:
        return 0
    }
  }

  useEffect(() => {
    if (survey && mode === "edit") {
      const check =
        survey.surveyStatusId === SurveyStatus.IN_PROGRESS &&
        checkAccess(user?.roles, [permissionsHH.moduleId], permissionsHH.subModule, "edit")
      !check && changeMode(hash)
    }
  }, [survey, user])

  if (errorSurvey) return <ErrorState />

  return (
    <div>
      {isLoadingSurvey && <Loading />}
      <Tabs selected={selectTab()} disableAnimation>
        <Tab label="Details" href="" role="tab" selected tabIndex={0} onClick={() => navigate(`#details`)}>
          <SurveyDetails survey={survey} mode={mode} changeMode={() => changeMode("#details")} />
        </Tab>
        <Tab
          label="Data loader"
          href=""
          role="tab"
          selected={false}
          tabIndex={0}
          onClick={() => navigate(`#data-loader`)}
        >
          <SurveyDataLoader survey={survey} mode={mode} changeMode={() => changeMode("#data-loader")} />
        </Tab>
        <Tab label="Data" href="" role="tab" selected={false} tabIndex={0} onClick={() => navigate(`#data`)}>
          <SurveyViewData survey={survey} />
        </Tab>
      </Tabs>
    </div>
  )
}

export default SurveyDetailsPage
